import React from 'react';
import { Helmet } from 'react-helmet-async';

const BASE_URL = 'https://presencia-app.com';
const DEFAULT_OG_IMAGE = `${BASE_URL}/hero.png`;

interface SEOProps {
  title: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  noIndex?: boolean;
  faqItems?: Array<{ question: string; answer: string }>;
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  noIndex = false,
  faqItems,
}) => {
  const fullTitle = `${title} | Presencia`;
  const canonicalUrl = canonical ? `${BASE_URL}${canonical}` : undefined;

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Presencia',
    url: BASE_URL,
    logo: `${BASE_URL}/logo.png`,
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@presencia-app.com',
      contactType: 'customer support',
    },
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Presencia',
    url: BASE_URL,
  };

  const faqSchema = faqItems && faqItems.length > 0
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqItems.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      }
    : null;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name='description' content={description} />}
      {noIndex && <meta name='robots' content='noindex, nofollow' />}
      {canonicalUrl && <link rel='canonical' href={canonicalUrl} />}

      {/* Open Graph */}
      <meta property='og:title' content={fullTitle} />
      {description && <meta property='og:description' content={description} />}
      <meta property='og:type' content='website' />
      {canonicalUrl && <meta property='og:url' content={canonicalUrl} />}
      <meta property='og:image' content={ogImage} />

      {/* Twitter Card */}
      <meta name='twitter:card' content='summary_large_image' />
      <meta name='twitter:title' content={fullTitle} />
      {description && <meta name='twitter:description' content={description} />}
      <meta name='twitter:image' content={ogImage} />

      {/* Structured Data */}
      <script type='application/ld+json'>
        {JSON.stringify(organizationSchema)}
      </script>
      <script type='application/ld+json'>
        {JSON.stringify(websiteSchema)}
      </script>
      {faqSchema && (
        <script type='application/ld+json'>
          {JSON.stringify(faqSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
